import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpUtilsService } from './http-utils.service';
import { catchError, map, mergeMap, Observable } from 'rxjs';
import { JsonConvert } from 'json2typescript';
import { Paging } from '../../model/paging/paging';
import { environment } from '../../../environment/environment';
import { Identity, IdentityArray, IdentityListArray, IdentityListData } from '../../model/identity/identity';
import { DeviceAccount, DeviceAccountArray } from '../../model/identity/device-account';
import {
	CaptiveDeviceAccount,
	CaptiveDeviceAccountArrayData,
	CaptiveDeviceAccountData,
} from '../../model/identity/captive-device-account';
import { ClientSession, ClientSessionListData } from '../../model/client/session';
import { ClientsCounters, ClientsCountersData } from '../../model/client/counters';
import { Client, ClientsData } from '../../model/client/client';
import { ServiceTypeEnum } from '../../model/enum/service-type.enum';
import { PasspointDeviceAccount, PasspointDeviceAccountData } from '../../model/identity/passpoint-device-account';
import { any } from '@amcharts/amcharts5/.internal/core/util/Array';

@Injectable({
	providedIn: 'root'
})

export class IdentityService {
	private _url: string = `${environment.baseAPI}/locations-groups/identities`;

	constructor(private _http: HttpClient, private _httpUtils: HttpUtilsService) { }


	GETIdentities(since: string, until: string, paging: Paging, mkt_consent?: boolean, prf_consent?: boolean, location_id?: string): Observable<Identity | any> {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap(httpHeaders => {
					return this._http.get(`${this._url}${paging.getQs()}&since=${since}&until=${until}${location_id ? `&location_id=${location_id}` : ''}${mkt_consent ? `&mkt_consent=${mkt_consent}` : ''}${prf_consent ? `&prf_consent=${prf_consent}` : ''}`, { headers: httpHeaders }).pipe(
						map((res: any) => {
							const jsonConvert: JsonConvert = new JsonConvert();
							const response: IdentityListData = jsonConvert.deserializeObject(res, IdentityListData);
							if (response.success) {
								return response;
							} else {
								// this._httpUtils.handleError(response);
								return;
							}
						}, catchError(err => {
							if (err.status === 401) { // Unauthorized
								this._httpUtils.logout();
							}
							throw err;
						})));
				}), catchError(err => {
					throw err;
				})
			);
		} catch (error) {
			throw error;
		}
	}
	GETIdentitiesAll(since: string, until: string, location_id?: string): Observable<Identity | any> {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap(httpHeaders => {
					return this._http.get(`${this._url}/all?since=${since}&until=${until}${location_id ? `&location_id=${location_id}` : ''}`, { headers: httpHeaders }).pipe(
						map((res: any) => {
							const jsonConvert: JsonConvert = new JsonConvert();
							const response: IdentityArray = jsonConvert.deserializeObject(res, IdentityArray);
							if (response.success) {
								return response;
							} else {
								// this._httpUtils.handleError(response);
								return;
							}
						}, catchError(err => {
							if (err.status === 401) { // Unauthorized
								this._httpUtils.logout();
							}
							throw err;
						})));
				}), catchError(err => {
					throw err;
				})
			);
		} catch (error) {
			throw error;
		}
	}
	GETDeviceAccounts(profile_id: string): Observable<DeviceAccount | any> {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap(httpHeaders => {
					const passpointUrl = `${this._url}/${profile_id}/device-accounts/passpoint`;
					return this._http.get(passpointUrl, { headers: httpHeaders }).pipe(
						map((res: any) => {
							const jsonConvert: JsonConvert = new JsonConvert();
							const response: DeviceAccountArray = jsonConvert.deserializeObject(res, DeviceAccountArray);
							if (response.success) {
								return response;
							} else {
								this._httpUtils.handleError(response);
								return;
							}
						}, catchError(err => {
							if (err.status === 401) { // Unauthorized
								this._httpUtils.logout();
							}
							throw err;
						}))
					);
				}), catchError(err => {
					throw err;
				})
			);
		} catch (error) {
			throw error;
		}
	}
	GETDeviceAccountsCaptive(profile_id: string): Observable<CaptiveDeviceAccountArrayData | any> {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap(httpHeaders => {
					return this._http.get(`${this._url}/${profile_id}/device-accounts/captive`, { headers: httpHeaders }).pipe(
						map((res: any) => {
							const jsonConvert: JsonConvert = new JsonConvert();
							const response: CaptiveDeviceAccountArrayData = jsonConvert.deserializeObject(res, CaptiveDeviceAccountArrayData);
							if (response.success) {
								return response;
							} else {
								this._httpUtils.handleError(response);
								return;
							}
						}, catchError(err => {
							if (err.status === 401) { // Unauthorized
								this._httpUtils.logout();
							}
							throw err;
						})));
				}), catchError(err => {
					throw err;
				})
			);
		} catch (error) {
			throw error;
		}
	}
	PUTDeviceUntil(profile_id: string, username: string, body: any) {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap(httpHeaders => {
					return this._http.put(`${this._url}/${profile_id}/device-accounts/${username}`, body, { headers: httpHeaders }).pipe(
						map((res: any) => {
							return res;
						}, catchError(err => {
							if (err.status === 401) { // Unauthorized
								this._httpUtils.logout();
							}
							throw err;
						})));
				}), catchError(err => {
					throw err;
				})
			);
		} catch (error) {
			throw error;
		}
	}
	PUTDisableDevice(profile_id: string) {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap(httpHeaders => {
					return this._http.put(`${this._url}/${profile_id}/device-accounts/disable`, null, { headers: httpHeaders }).pipe(
						map((res: any) => {
							return res;
						}, catchError(err => {
							if (err.status === 401) { // Unauthorized
								this._httpUtils.logout();
							}
							throw err;
						})));
				}), catchError(err => {
					throw err;
				})
			);
		} catch (error) {
			throw error;
		}
	}
	PUTDisableSingleDevice(profile_id: string, username: string, service_type: ServiceTypeEnum.PASSPOINT | ServiceTypeEnum.CAPTIVE) {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap(httpHeaders => {
					return this._http.put(`${this._url}/${profile_id}/device-accounts/${username}/disable?service_type=${service_type}`, null, { headers: httpHeaders }).pipe(
						map((res: any) => {
							return res;
						}, catchError(err => {
							if (err.status === 401) { // Unauthorized
								this._httpUtils.logout();
							}
							throw err;
						})));
				}), catchError(err => {
					throw err;
				})
			);
		} catch (error) {
			throw error;
		}
	}
	GETPasspointCount(since: string, until: string, location_id?: string): Observable<Identity | any> {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap(httpHeaders => {
					return this._http.get(`${this._url}/counters?${location_id ? `location_id=${location_id}` : ''}${since ? `&since=${since}` : ''}${until ? `&until=${until}` : ''}`, { headers: httpHeaders }).pipe(
						map((res: any) => {
							const jsonConvert: JsonConvert = new JsonConvert();
							const response: ClientsCountersData = jsonConvert.deserializeObject(res, ClientsCountersData);
							if (response.success) {
								return response;
							} else {
								this._httpUtils.handleError(response);
								return;
							}
						}, catchError(err => {
							if (err.status === 401) { // Unauthorized
								this._httpUtils.logout();
							}
							throw err;
						})));
				}), catchError(err => {
					throw err;
				})
			);
		} catch (error) {
			throw error;
		}
	}
	GETPasspointSessions(paging: Paging, client_id: string, time_range?: string, since?: string, until?: string): Observable<ClientSession | any> {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap(httpHeaders => {
					return this._http.get(`${this._url}/${client_id}/sessions${paging.getQs()}&time_range=${time_range}&since=${since}&until=${until}`, { headers: httpHeaders }).pipe(
						map((res: any) => {
							const jsonConvert: JsonConvert = new JsonConvert();
							const response: ClientSessionListData = jsonConvert.deserializeObject(res, ClientSessionListData);
							if (response.success) {
								return response;
							} else {
								this._httpUtils.handleError(response);
								return;
							}
						}, catchError(err => {
							if (err.status === 401) { // Unauthorized
								this._httpUtils.logout();
							}
							throw err;
						})));
				}), catchError(err => {
					throw err;
				})
			);
		} catch (error) {
			throw error;
		}
	}
	GETClientPasspoint(client_id: string): Observable<Client | any> {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap(httpHeaders => {
					return this._http.get(`${this._url}/${client_id}`, { headers: httpHeaders }).pipe(
						map((res: any) => {
							const jsonConvert: JsonConvert = new JsonConvert();
							const response: ClientsData = jsonConvert.deserializeObject(res, ClientsData);
							if (response.success) {
								return response;
							} else {
								this._httpUtils.handleError(response);
								return;
							}
						}, catchError(err => {
							if (err.status === 401) { // Unauthorized
								this._httpUtils.logout();
							}
							throw err;
						})));
				}), catchError(err => {
					throw err;
				})
			);
		} catch (error) {
			throw error;
		}
	}


	GETDeviceAccountCaptiveService(identity_id: string, username: string): Observable<CaptiveDeviceAccountData | any> {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap(httpHeaders => {
					return this._http.get(`${this._url}/${identity_id}/device-accounts/${username}/captive`, { headers: httpHeaders }).pipe(
						map((res: any) => {
							const jsonConvert: JsonConvert = new JsonConvert();
							const response: CaptiveDeviceAccountData = jsonConvert.deserializeObject(res, CaptiveDeviceAccountData);
							if (response) {
								return response
							} else {
								this._httpUtils.handleError(response)
								return;
							}
						}, catchError(err => {
							if (err.status === 401) {
								this._httpUtils.logout()
							}
							throw err;
						}))
					);
				}),
			);
		} catch (error) {
			throw error;
		}
	}

	GETDeviceAccountPasspointService(identity_id: string, username: string): Observable<PasspointDeviceAccountData | any> {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap(httpHeaders => {
					return this._http.get(`${this._url}/${identity_id}/device-accounts/${username}/passpoint`, { headers: httpHeaders }).pipe(
						map((res: any) => {
							const jsonConvert: JsonConvert = new JsonConvert();
							const response: PasspointDeviceAccountData = jsonConvert.deserializeObject(res, PasspointDeviceAccountData);
							if (response) {
								return response
							} else {
								this._httpUtils.handleError(response)
								return;
							}
						}, catchError(err => {
							if (err.status === 401) {
								this._httpUtils.logout()
							}
							throw err;
						}))
					);
				}),
			);
		} catch (error) {
			throw error;
		}
	}

	GETDeviceAccountService(identity_id: string, username: string): Observable<any> {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap(httpHeaders => {
					return this._http.get(`${this._url}/${identity_id}/device-accounts/${username}`, { headers: httpHeaders }).pipe(
						map((res: any) => {
							const jsonConvert: JsonConvert = new JsonConvert();
							return res;
						}, catchError(err => {
							if (err.status === 401) {
								this._httpUtils.logout()
							}
							throw err;
						}))
					);
				}),
			);
		} catch (err) {
			throw err;
		}
	}

}
